import { createElement, useEffect } from 'react';
import { Link, Routes, Route, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Boxes,
  ChevronRight,
  Network,
  Layers,
  LayoutDashboard,
  Activity,
  Server,
  Cpu,
  Camera,
  ScrollText,
  Settings,
} from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { StatusPill } from '@vnetwork/ui/data';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useMyServices, serviceLabel, serviceProviderScope } from '@/api/services';
import { KUBERNETES_SCOPE } from '@/api/k8s';
import { useSetContextualNav } from '@/components/layout/nav-context';
import { ClusterList } from './ClusterList';
import { ClusterCreate } from './ClusterCreate';
import { ClusterDetail } from './ClusterDetail';
import { KubernetesDashboard } from './KubernetesDashboard';
import { K8sNetworks, K8sResources } from './KubernetesExtras';
import { KubernetesActivateLanding } from './KubernetesActivateLanding';
import { KubernetesServiceSettings } from './KubernetesServiceSettings';

const BASE = '/services/kubernetes';

/**
 * Managed Kubernetes module — service_uuid-scoped (Internal.Opennebula.Kubernetes).
 *   /services/kubernetes                                  → picker
 *   /services/kubernetes/:serviceId                       → service dashboard
 *   /services/kubernetes/:serviceId/clusters              → cluster list
 *   /services/kubernetes/:serviceId/clusters/create       → create wizard
 *   /services/kubernetes/:serviceId/clusters/:clusterId/* → cluster detail (resource tabs)
 *
 * Cluster routes nest under `clusters/` (mirroring Compute's `instances/:instanceId`)
 * so a clusterId can never collide with a service-scope slug.
 *
 * The service-scope nav (Clusters / Networks / Resources) lives in the app's main
 * `ContextualSidebar`, registered by {@link K8sServiceShell}. Inside a cluster the
 * SAME registration swaps to the cluster's sub-sections (Overview, Metrics, …) with
 * a "Back to clusters" link — one registration point, because a child registration
 * would be clobbered by this shell's effect re-running on every pathname change.
 */
export function KubernetesPage() {
  return (
    <Routes>
      <Route index element={<Picker />} />
      <Route path=":serviceId" element={<K8sServiceShell />}>
        <Route index element={<KubernetesDashboard />} />
        <Route path="clusters" element={<ClusterList />} />
        <Route path="clusters/create" element={<ClusterCreate />} />
        <Route path="clusters/:clusterId/*" element={<ClusterDetail />} />
        <Route path="networks" element={<K8sNetworks />} />
        <Route path="resources" element={<K8sResources />} />
        <Route path="settings" element={<KubernetesServiceSettings />} />
      </Route>
    </Routes>
  );
}

const SERVICE_NAV = [
  { slug: '', label: 'Dashboard', icon: LayoutDashboard },
  { slug: 'clusters', label: 'Clusters', icon: Boxes },
  { slug: 'networks', label: 'Networks', icon: Network },
  { slug: 'resources', label: 'Resources', icon: Layers },
  { slug: 'settings', label: 'Settings', icon: Settings },
];

/** Cluster-scope sidebar entries — mirror ClusterDetail's nested routes. */
const CLUSTER_NAV = [
  { slug: '', label: 'Overview', icon: LayoutDashboard },
  { slug: 'metrics', label: 'Metrics', icon: Activity },
  { slug: 'pools', label: 'Node pools', icon: Layers },
  { slug: 'nodes', label: 'Nodes', icon: Server },
  { slug: 'instances', label: 'Instances', icon: Cpu },
  { slug: 'ingress', label: 'Ingress', icon: Network },
  { slug: 'snapshots', label: 'Snapshots', icon: Camera },
  { slug: 'logs', label: 'Logs', icon: ScrollText },
  { slug: 'settings', label: 'Settings', icon: Settings },
];

/** Registers the Kubernetes scope sidebar (service or cluster), then renders the active route. */
function K8sServiceShell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `${BASE}/${serviceId}`;
  const hrefFor = (slug: string) => (slug ? `${base}/${slug}` : base);

  useSetContextualNav(() => {
    const segments = pathname.slice(base.length).split('/').filter(Boolean);
    // Cluster scope lives under /clusters/:clusterId (not /create).
    const clusterId =
      segments[0] === 'clusters' && segments[1] && segments[1] !== 'create' ? segments[1] : null;

    // Cluster scope — the detail page's former in-content tabs, as sidebar entries.
    if (clusterId) {
      const clusterBase = `${base}/clusters/${clusterId}`;
      const clusterHref = (slug: string) => (slug ? `${clusterBase}/${slug}` : clusterBase);
      const sub = segments[2] ?? '';
      const known = CLUSTER_NAV.some((n) => n.slug === sub);
      return {
        sections: [
          {
            heading: 'Cluster',
            items: CLUSTER_NAV.map((n) => ({
              id: clusterHref(n.slug),
              label: n.label,
              icon: createElement(n.icon, { size: 16 }),
              onSelect: () => navigate(clusterHref(n.slug)),
            })),
          },
        ],
        // Unknown sub-paths render Overview (ClusterDetail's catch-all route).
        activeId: known ? clusterHref(sub) : clusterBase,
        back: { label: 'Back to clusters', onSelect: () => navigate(hrefFor('clusters')) },
      };
    }

    const activeId = pathname.startsWith(`${base}/networks`)
      ? hrefFor('networks')
      : pathname.startsWith(`${base}/resources`)
        ? hrefFor('resources')
        : pathname.startsWith(`${base}/settings`)
          ? hrefFor('settings')
          : pathname === base || pathname === `${base}/`
            ? base
            : hrefFor('clusters'); // clusters / create
    return {
      sections: [
        {
          items: SERVICE_NAV.map((n) => ({
            id: hrefFor(n.slug),
            label: n.label,
            icon: createElement(n.icon, { size: 16 }),
            onSelect: () => navigate(hrefFor(n.slug)),
          })),
        },
      ],
      activeId,
      back: { label: 'Back to Services', onSelect: () => navigate('/services') },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, pathname, navigate]);

  return <Outlet />;
}

function Picker() {
  const navigate = useNavigate();
  const { data: all = [], isLoading, isError, refetch } = useMyServices();
  const services = all.filter((s) => serviceProviderScope(s) === KUBERNETES_SCOPE);

  useEffect(() => {
    if (!isLoading && !isError && services.length === 1) navigate(`${BASE}/${services[0].id}`, { replace: true });
  }, [isLoading, isError, services, navigate]);

  // No services yet — show the activation hero instead of a plain empty-state.
  if (!isLoading && !isError && services.length === 0) {
    return <KubernetesActivateLanding />;
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Boxes size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to="/services">Services</Link></BreadcrumbItem>
            <BreadcrumbItem current>Managed Kubernetes</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Managed Kubernetes"
        description="Clusters with node pools, ingress and auto-scaling."
      />
      <Section title="Your Kubernetes services" description="Pick a service to manage its clusters.">
        {isError ? (
          <Alert tone="danger" title="Couldn't load services" actions={<Button size="sm" variant="secondary" onClick={() => refetch()}>Retry</Button>}>
            The service catalog is temporarily unavailable.
          </Alert>
        ) : isLoading ? (
          <Skeleton style={{ height: 116 }} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--vn-space-4)' }}>
            {services.map((svc) => (
              <button key={svc.id} type="button" style={card} onClick={() => navigate(`${BASE}/${svc.id}`)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', marginBottom: 'var(--vn-space-3)' }}>
                  <span style={iconWrap}><Boxes size={20} /></span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{serviceLabel(svc)}</div>
                    <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{svc.provider?.display_name ?? 'Managed Kubernetes'}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <StatusPill tone={svc.status === 'active' ? 'success' : 'muted'}>{svc.status || 'Unknown'}</StatusPill>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>Open <ChevronRight size={14} /></span>
                </div>
              </button>
            ))}
          </div>
        )}
      </Section>
    </Container>
  );
}

const card: React.CSSProperties = { display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'var(--vn-fg-default)', border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-lg)', background: 'var(--vn-bg-surface)', padding: 'var(--vn-space-4)' };
const iconWrap: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 'var(--vn-radius-md)', background: 'var(--vn-bg-muted, var(--vn-bg-hover))', flexShrink: 0 };

export default KubernetesPage;
